import { FC, KeyboardEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@kizen/kds/Button';
import { ExpandingTextArea } from 'ts-components/ExpandingTextArea';
import { isNonComposingEnterKeyboardEvent } from 'utility/keyboard';
import {
  composerControls,
  draftsAfter,
  resolveComposerSend,
  withReturnedText,
  withoutSentText,
  type ComposerDraft,
} from './composerSend';

const NO_DRAFTS: readonly ComposerDraft[] = [];
/** The composer textarea's id, so other panel UI can tell when it holds focus. */
export const COMPOSER_INPUT_ID = 'pi-chat-composer-input';
import { ComposerRoot, ComposerSend, ComposerStatus } from './styles';

export interface PiChatComposerProps {
  placeholder: string;
  disabledMessage?: string;
  /** Blocks typing and sending while keeping Stop available. */
  lockedMessage?: string;
  canSend: boolean;
  onSend: (text: string) => Promise<boolean>;
  onFocus: () => void;
  returnedDrafts?: readonly ComposerDraft[];
  isStreaming: boolean;
  onStop: () => void;
}

export const PiChatComposer: FC<PiChatComposerProps> = ({
  placeholder,
  disabledMessage,
  lockedMessage,
  canSend,
  onSend,
  onFocus,
  returnedDrafts = NO_DRAFTS,
  isStreaming,
  onStop,
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const [notSent, setNotSent] = useState(false);
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  const [lastTakenDraftId, setLastTakenDraftId] = useState<string | null>(null);
  const newDrafts = draftsAfter(returnedDrafts, lastTakenDraftId);
  if (newDrafts.length > 0) {
    setLastTakenDraftId(newDrafts[newDrafts.length - 1].id);
    const returned = newDrafts.map((draft) => draft.text).join('\n');
    setValue((current) => withReturnedText(current, returned));
    setNotSent(true);
  }
  const { inputDisabledMessage, showStop } = composerControls({
    disabledMessage,
    lockedMessage,
    isStreaming,
  });
  const disabled = Boolean(inputDisabledMessage);
  const sendable = !disabled && canSend && !sending && value.trim().length > 0;
  const statusText = notSent ? t('Not sent') : '';

  const handleChange = (next: string) => {
    setValue(next);
    setNotSent(false);
  };

  const send = async () => {
    if (!sendable || sendingRef.current) return;
    const text = value;
    sendingRef.current = true;
    setSending(true);
    setNotSent(false);
    let sent = false;
    try {
      sent = await onSend(text.trim());
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
    const result = resolveComposerSend(text, sent);
    setValue((current) =>
      result.notSent ? current : withoutSentText(current, text)
    );
    if (result.notSent) setNotSent(true);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (isNonComposingEnterKeyboardEvent(event) && !event.shiftKey) {
      event.preventDefault();
      void send();
    }
  };

  return (
    <ComposerRoot>
      <ComposerSend>
        <ComposerStatus
          $tone={notSent ? 'error' : 'muted'}
          role="status"
          aria-live="polite"
          data-qa-id="pi-chat-composer-status"
        >
          {statusText}
        </ComposerStatus>
        {showStop ? (
          <Button
            variant="text"
            color="warning"
            size="small"
            onClick={onStop}
            qa={{ id: 'pi-chat-stop' }}
          >
            {t('Stop')}
          </Button>
        ) : null}
      </ComposerSend>
      <div onFocus={onFocus}>
        <ExpandingTextArea
          id={COMPOSER_INPUT_ID}
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          disabled={disabled}
          disabledMessage={inputDisabledMessage}
          disableSendButton={!sendable}
          onKeyDown={handleKeyDown}
          handleSubmit={() => void send()}
          showSubmitButton
        />
      </div>
    </ComposerRoot>
  );
};
